import { Fragment } from 'react'

import { Borrow } from '../../logic/assets'
import { ApyWithRewardsCell } from '../ApyWithRewardsCell'
import { BorrowActionButton, RepayActionButton } from './components/ActionButton'
import { EModeIndicator } from './components/EModeIndicator'
import { sortByAPY, sortByUsdValue } from '@/domain/common/sorters'
import { OpenDialogFunction } from '@/domain/state/dialogs/slice'
import { EModeCategoryId } from '@/features/sparklend/common/data/market-info/emode'
import { eModeDialogConfig } from '@/features/sparklend/dialogs/e-mode/EModeDialog'
import { ProxyMarketActionsCell } from '@/features/sparklend/my-portfolio/components/borrow-table/components/ProxyMarketActionsCell'
import { Panel } from '@/ui/atoms/panel/Panel'
import { ApyTooltip } from '@/ui/molecules/apy-tooltip/ApyTooltip'
import { ActionsCell } from '@/ui/molecules/data-table/components/ActionsCell'
import { AmountCell } from '@/ui/molecules/data-table/components/AmountCell'
import { TokenWithLogo } from '@/ui/molecules/data-table/components/TokenWithLogo'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { testIds } from '@/ui/utils/testIds'

export interface BorrowTableProps {
  assets: Borrow[]
  openDialog: OpenDialogFunction
  eModeCategoryId: EModeCategoryId
  chainId: number
}

export function BorrowTable({ assets, openDialog, eModeCategoryId, chainId }: BorrowTableProps) {
  return (
    <Panel className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <h3 className="typography-heading-4 text-primary">Borrow</h3>
        <EModeIndicator
          eModeCategoryId={eModeCategoryId}
          onButtonClick={() => {
            openDialog(eModeDialogConfig, { userEModeCategoryId: eModeCategoryId })
          }}
        />
      </div>
      <ResponsiveDataTable
        gridTemplateColumnsClassName="grid-cols-[repeat(4,1fr)_2fr]"
        columnDefinition={{
          symbol: {
            header: 'Assets',
            renderCell: ({ token, reserve }) => (
              <TokenWithLogo token={token} reserveStatus={reserve.status} chainId={chainId} from="/my-portfolio" />
            ),
          },
          inWallet: {
            header: 'Available',
            sortable: true,
            sortingFn: (a, b) => sortByUsdValue(a.original, b.original, 'available'),
            headerAlign: 'right',
            renderCell: ({ token, available }, mobileViewOptions) => (
              <AmountCell
                token={token}
                amount={available}
                mobileViewOptions={mobileViewOptions}
                formattingOptions={{ showSymbol: 'only-tooltip-content' }}
              />
            ),
          },
          deposit: {
            header: 'Your borrow',
            sortable: true,
            sortingFn: (a, b) => sortByUsdValue(a.original, b.original, 'debt'),
            headerAlign: 'right',
            renderCell: ({ token, debt }, mobileViewOptions) => (
              <AmountCell
                token={token}
                amount={debt}
                mobileViewOptions={mobileViewOptions}
                formattingOptions={{ showSymbol: 'only-tooltip-content' }}
              />
            ),
          },
          apy: {
            header: <ApyTooltip variant="borrow">APY</ApyTooltip>,
            headerAlign: 'right',
            sortable: true,
            sortingFn: (a, b) => sortByAPY(a.original.borrowAPY, b.original.borrowAPY),
            renderCell: ({ borrowAPY, sparkRewards }, mobileViewOptions, index) => (
              <ApyWithRewardsCell
                baseApy={borrowAPY}
                sparkRewards={sparkRewards}
                mobileViewOptions={mobileViewOptions}
                {...(index !== undefined && { apyDataTestId: testIds.myPortfolio.borrowAPY(index) })}
              />
            ),
          },
          actions: {
            header: '',
            renderCell: ({ token, debt, reserve, proxyMarket }) => {
              return proxyMarket ? (
                <ProxyMarketActionsCell>
                  {[token, proxyMarket.token].map((token) => (
                    <Fragment key={token.address}>
                      <BorrowActionButton
                        reserve={reserve}
                        initialBorrowToken={token}
                        openDialog={openDialog}
                        showTokenIcon
                      />
                      <RepayActionButton
                        reserve={reserve}
                        initialRepayToken={token}
                        openDialog={openDialog}
                        debt={debt}
                        showTokenIcon
                      />
                    </Fragment>
                  ))}
                </ProxyMarketActionsCell>
              ) : (
                <ActionsCell>
                  <BorrowActionButton reserve={reserve} initialBorrowToken={token} openDialog={openDialog} />
                  <RepayActionButton reserve={reserve} initialRepayToken={token} openDialog={openDialog} debt={debt} />
                </ActionsCell>
              )
            },
          },
        }}
        data={assets}
      />
    </Panel>
  )
}
